import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import Icon from '../common/Icons';
import { STOCK_LOCATIONS } from '../../constants/siteCareModes';
import {
  getMedicationAvailabilityMeta,
  resolveMedicationStockRow
} from './utils/medicationStockDisplay';

const MAX_SUGGESTIONS = 18;

function medMatchesQuery(med, q) {
  if (!q) return true;
  const name = (med.name || '').toLowerCase();
  const base = (med.base_name || '').toLowerCase();
  const loc = (med.location_label || '').toLowerCase();
  return name.includes(q) || (base && base.includes(q)) || (loc && loc.includes(q));
}

function StockLocationBadge({ label, stockLocation }) {
  if (!label) return null;
  const isDuty = stockLocation === STOCK_LOCATIONS.DUTY;
  return (
    <span
      className={[
        'rx-med-search__loc',
        isDuty ? 'rx-med-search__loc--duty' : 'rx-med-search__loc--main'
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function MedicationAvailabilityMeta({ stockRow, medFallback = null, compact = false }) {
  const meta = getMedicationAvailabilityMeta(stockRow, medFallback);
  const hasContent =
    stockRow != null || meta.priceLabel || meta.expiryLabel || meta.qtyLabel !== '—';

  if (!hasContent) {
    return compact ? null : <span className="rx-med-search__avail rx-med-search__avail--unknown">—</span>;
  }

  return (
    <span
      className={[
        'rx-med-search__avail',
        `rx-med-search__avail--${meta.tone}`,
        compact ? 'rx-med-search__avail--compact' : ''
      ]
        .filter(Boolean)
        .join(' ')}
      title={[meta.qtyLabel, meta.priceLabel, meta.expiryLabel, meta.statusLabel].filter(Boolean).join(' · ')}
    >
      <span className="rx-med-search__avail-row">
        <span className="rx-med-search__avail-qty">{meta.qtyLabel}</span>
        {meta.priceLabel ? (
          <span className="rx-med-search__avail-price">{meta.priceLabel}</span>
        ) : null}
      </span>
      {meta.expiryLabel ? (
        <span className={`rx-med-search__avail-expiry rx-med-search__avail-expiry--${meta.expiryTone}`}>
          {meta.expiryLabel}
        </span>
      ) : null}
      {!compact && meta.statusLabel && meta.tone !== 'ok' ? (
        <span className="rx-med-search__avail-status">{meta.statusLabel}</span>
      ) : null}
    </span>
  );
}

/**
 * Recherche médicament — liste déroulante avec stock, prix et péremption site.
 * Sur site centrale + armoire : entrées en double avec badge PHARMACIE / ARMOIRE DE GARDE.
 */
export default function RxMedicationSearch({
  medications = [],
  value = '',
  onChange,
  onSelect,
  selectedMed = null,
  stockByMedicationId = {},
  stockSiteLabel = '',
  stockLoading = false,
  loading = false,
  disabled = false,
  placeholder = 'Rechercher un médicament…'
}) {
  const listId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const suggestions = useMemo(() => {
    const q = value.trim().toLowerCase();
    let list = medications;
    if (q) {
      list = medications.filter((m) => medMatchesQuery(m, q));
    }
    return list.slice(0, MAX_SUGGESTIONS);
  }, [medications, value]);

  const stockReady = Boolean(stockSiteLabel) && !stockLoading;
  const selectedStock = selectedMed
    ? resolveMedicationStockRow(stockByMedicationId, selectedMed, stockReady)
    : null;

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (med) => {
    onSelect?.(med);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      pick(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const optionKey = (med) => med.catalogKey || String(med.id);
  const isSameMed = (a, b) => {
    if (!a || !b) return false;
    if (a.catalogKey && b.catalogKey) return a.catalogKey === b.catalogKey;
    return String(a.id) === String(b.id) && String(a.stock_location || '') === String(b.stock_location || '');
  };

  return (
    <div className="rx-med-search" ref={wrapRef}>
      {stockSiteLabel ? (
        <p className="rx-med-search__site-stock">
          {stockSiteLabel}
          {stockLoading ? <span className="rx-med-search__site-stock-loading"> · …</span> : null}
        </p>
      ) : null}

      <div className={`rx-med-search__field${selectedMed ? ' is-valid' : ''}`}>
        <Icon name="search" size={15} className="rx-med-search__icon" aria-hidden />
        <input
          type="search"
          className="rx-med-search__input"
          value={value}
          onChange={(e) => {
            onChange?.(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
        />
        {loading ? <span className="rx-med-search__spinner" aria-hidden /> : null}
      </div>

      {selectedMed ? (
        <div
          className={[
            'rx-med-search__picked',
            selectedMed.stock_location === STOCK_LOCATIONS.DUTY ? 'rx-med-search__picked--duty' : '',
            selectedMed.stock_location === STOCK_LOCATIONS.MAIN && selectedMed.location_label
              ? 'rx-med-search__picked--main'
              : ''
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <p className="rx-med-search__picked-name">
            <Icon name="checkCircle" size={14} aria-hidden />
            <span className="rx-med-search__picked-title">
              {selectedMed.base_name || selectedMed.name}
            </span>
            <StockLocationBadge
              label={selectedMed.location_label}
              stockLocation={selectedMed.stock_location}
            />
            {selectedMed.unit_name ? (
              <span className="rx-med-search__unit"> · {selectedMed.unit_name}</span>
            ) : null}
          </p>
          <MedicationAvailabilityMeta
            stockRow={selectedStock}
            medFallback={selectedMed}
            compact
          />
        </div>
      ) : null}

      {open && !loading && suggestions.length > 0 ? (
        <ul id={listId} className="rx-med-search__list" role="listbox">
          {suggestions.map((med, index) => {
            const stockRow = resolveMedicationStockRow(stockByMedicationId, med, stockReady);
            const isDuty = med.stock_location === STOCK_LOCATIONS.DUTY;
            const isMainDual = Boolean(med.location_label) && !isDuty;
            return (
              <li key={optionKey(med)} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index || isSameMed(selectedMed, med)}
                  className={[
                    'rx-med-search__option',
                    isDuty ? 'rx-med-search__option--duty' : '',
                    isMainDual ? 'rx-med-search__option--main' : '',
                    activeIndex === index ? 'is-active' : '',
                    isSameMed(selectedMed, med) ? 'is-selected' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(med)}
                >
                  <span className="rx-med-search__option-main">
                    <span className="rx-med-search__option-name-row">
                      <span className="rx-med-search__option-name">
                        {med.base_name || med.name}
                      </span>
                      <StockLocationBadge label={med.location_label} stockLocation={med.stock_location} />
                    </span>
                    {med.unit_name ? (
                      <span className="rx-med-search__option-meta">{med.unit_name}</span>
                    ) : null}
                  </span>
                  <MedicationAvailabilityMeta stockRow={stockRow} medFallback={med} />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {open && !loading && value.trim() && suggestions.length === 0 ? (
        <p className="rx-med-search__empty">Aucun médicament trouvé.</p>
      ) : null}
    </div>
  );
}
